import axios from 'axios';
import { xmlToJson, isEmptyString, DHLError, CreatePolicyError, CreatePolicyTimeoutError, axiosRequest, isSuccessfulResponse, handleHttpResponse } from '@general/js/utils';
import Services from '@general/js/services/Services';
import { LoadingStatus, PolicyErrorCodesEnum } from '@general/js/enums';
import { EnvironmentVariables } from '@general/js/environmentVariables';
import { CAPTURE_ADDITIONAL_INFORMATION } from '@crmVue/ChangeCover/src/constants/APIUrls';
import AbstractClass from './JoinNowAbstractServices';

export default class JoinNowServices extends AbstractClass {
  /**
   * Implicitly returns a Promise for fetching address details services.
   */
  static async getPredictiveAddress(addressLine) {
    // only call api if addressLine is not empty string. if addressLine is empty, return null
    if (addressLine !== '') {
      try {
        const queryParams = {
          params: {
            Method: Services.ADDRESS_VALIDATION.METHOD.PREDICTIVE,
            AddressLine: addressLine,
            RequestKey: Services.ADDRESS_VALIDATION.REQUEST_KEY,
          },
        };

        const response = await axios.get(Services.ADDRESS_VALIDATION.GET_ADDRESS_URL, queryParams);
        const responseInJson = xmlToJson(response.data);
        if (handleHttpResponse(response) && responseInJson.DtResponse.$.ErrorMessage === '') {
          return responseInJson;
        }

        throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
      } catch (error) {
        throw new DHLError(`getPredictiveAddress() - Call failed with error: ${JSON.stringify(error.message)}`, error);
      }
    }

    return null;
  }

  /**
   * Implicitly returns a Promise for fetching address details services.
   */
  static async getAddressDetails(RecordId) {
    try {
      const queryParams = {
        params: {
          Method: Services.ADDRESS_VALIDATION.METHOD.FULL,
          RecordId,
          RequestKey: Services.ADDRESS_VALIDATION.REQUEST_KEY,
        },
      };

      const response = await axios.get(Services.ADDRESS_VALIDATION.GET_ADDRESS_URL, queryParams);
      const responseInJson = xmlToJson(response.data);
      if (handleHttpResponse(response) && responseInJson.DtResponse.$.ErrorMessage === '') {
        return responseInJson;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`getAddressDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching Join Now reference data
   */
  static async getReferenceData() {
    try {
      const response = await axios.get(Services.JOIN_NOW.GET_REFERENCE_DATA);
      if (handleHttpResponse(response) && response.data.sex && response.data.sex.options) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`getReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching Join Now reference data
   */
  static async getMaintenanceData() {
    try {
      const response = await axios.get(Services.JOIN_NOW.GET_MAINTENANCE_DATA);
      if (handleHttpResponse(response)) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`getMaintenanceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching quote details
   */
  static async getQuoteDetails(quoteId) {
    try {
      const response = await axiosRequest().get(Services.JOIN_NOW.GET_QUOTE_DETAILS.replace('#{quoteID}', quoteId));
      if (handleHttpResponse(response) && response.data.quote && response.data.opportunity && response.data.opportunity.contact) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`getQuoteDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a promise for uploading a file through file upload API
   * @param {FormData} files - files to be uploaded
   * @param {string} contactIdOrMemberNumber - either GUID (contactId) for join online or member number for update member details
   * @param {string} fileType - define if file upload is for SERCAT or DVA
   * @returns
   */
  static async uploadDocument(files, contactIdOrMemberNumber, fileType) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      const URL = `${Services.JOIN_NOW.UPLOAD_FILE.replace('#{contactIdOrMemberNumber}', contactIdOrMemberNumber)}${fileType}`;

      const response = await axiosRequest().post(URL, files, config);
      // Receive status code 201 when response is successful
      if (isSuccessfulResponse(response)) {
        return 'success';
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`uploadDocument() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching product price
   * @param {object} payload - payload to fetch the product price
   */
  static async getProductPrice(payload) {
    try {
      const response = await axiosRequest().post(Services.JOIN_NOW.GET_PRODUCT_PRICE, payload);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getProductPrice() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * @deprecated Since Bambora has been replaced by Ipsi.
   * Convert Secure Transaction Token into permanent token and get the masked credit card information
   */
  static async convertSecureTransactionToken(stt) {
    try {
      const response = await axiosRequest().get(Services.JOIN_NOW.CONVERT_STT.replace('#{stt}', stt));
      if (handleHttpResponse(response)) {
        // Check if the response data contains non-null/non-empty responses.
        // NOTE: Do not check `response.data.declinedMessage` since it can be non-null/non-empty even when the other fields aren't.
        if (
          isEmptyString(response.data.creditCardToken) &&
          isEmptyString(response.data.cardType) &&
          isEmptyString(response.data.truncatedCard) &&
          isEmptyString(response.data.expiryYear) &&
          isEmptyString(response.data.expiryMonth)
        ) {
          throw new DHLError(`An invalid card value is returned: ${JSON.stringify(response.data)}`);
        }
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      throw new DHLError(`convertSecureTransactionToken() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching membership creation status
   * @param {object} payload - payload to fetch membership creation status
   */
  static async createMembership(payload) {
    try {
      const response = await axiosRequest().post(`${Services.JOIN_NOW.CREATE_MEMBERSHIP}`, payload, { timeout: Number(EnvironmentVariables.MemberPortalTimeout.TIMEOUT) });
      if (handleHttpResponse(response) && response.data.memberNumber && response.data.creationStatus) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`); // Rejects the promise
    } catch (error) {
      const policyErrorCode = error?.response?.data?.Error?.Code;
      if (error.code === PolicyErrorCodesEnum.TIMEOUT_ERROR) { // error.code is set by axios upon timeout
        throw new CreatePolicyTimeoutError(`createMembership() - Call failed with error: ${JSON.stringify(error.message)}`, error);
      } else if (policyErrorCode === PolicyErrorCodesEnum.CREATE_POLICY_ERROR) {
        throw new CreatePolicyError(`createMembership() - Call failed with error: ${JSON.stringify(error.response.data.Error.ErrorMessage)}`, error.response.data.Error);
      }
      throw new DHLError(`createMembership() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Upload file API for SERCAT - Change Cover CAI Eligibility Step
   * @param {Object} payload
   * @returns {String} 'success' when the API returns a successful response
   */
  static async uploadFile(payload) {
    try {
      const response = await axiosRequest().post(CAPTURE_ADDITIONAL_INFORMATION.UPLOAD_FILE, payload, {
        timeout: 60000, // 1min
      });

      if (isSuccessfulResponse(response)) {
        return LoadingStatus.SUCCESS;
      }

      throw new DHLError(`Issue with the response: ${response}`);
    } catch (error) {
      throw new DHLError(`uploadFile() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

   /**
   * Function to raise a work item for create policy flow in public website.
   *
   * @param {object} payload Failed payload when create policy flow fails 
   */
  static async raiseWorkItem(payload) {
    try {
      const response = await axiosRequest().post(Services.JOIN_NOW.POST_JOIN_NOW_WORK_ITEM, payload);

      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`raiseWorkItem() - call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
